import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { useGetFeatureAccess } from 'v2/hooks/useGetFeatureAccess';
import type { FeatureAccessStatus } from 'v2/interfaces/featureAccess';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import AnticipationCard from './AnticipationCard';
import AvailableCard from './AvailableCard';
import FutureCard from './FutureCard';
import TotalCard from './TotalCard';

function CardList() {
  const [hasValueToAnticipate, setHasValueToAnticipate] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [anticipateModal, setAnticipateModal] = useState(false);
  const [loadingTransferAvailable, setLoadingTransferAvailable] = useState(true);

  const { data: featureAccessData, refetch: refetchFeatureAccessData } = useGetFeatureAccess();

  const anticipationAccessData: FeatureAccessStatus = featureAccessData?.anticipation;

  const transferAccessData: FeatureAccessStatus = featureAccessData?.transfer;

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const cannotAntecipate = eduzzAccountsService.verifyUserHasBetaModule('cannot-antecipate');

  const [loadTransferAvailable] = useCallbackObservable(() => {
    setLoadingTransferAvailable(true);
    return financialStatementService.getAvailable().pipe(
      tap(({ data }) => {
        financialStatementService.setCurrentBalance(user?.crossborder ? data.total_origin : data.total);
        setLoadingTransferAvailable(false);
      }),
      catchError(error => {
        toast.error(error);
        setLoadingTransferAvailable(false);
        return of(true);
      })
    );
  }, []);

  const toggleTransferModal = () => setTransferModal(prevState => !prevState);
  const toggleAnticipationModal = () => setAnticipateModal(prevState => !prevState);

  const showAntecipate = !cannotAntecipate;

  return (
    <div
      id='card-list'
      className={`mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 ${showAntecipate ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
    >
      <TotalCard />
      <AvailableCard
        isLoading={loadingTransferAvailable}
        modalTransferIsOpen={transferModal}
        hasValueToAnticipate={hasValueToAnticipate}
        transferAccessData={transferAccessData}
        refetchFeatureAccessData={refetchFeatureAccessData}
        fetchData={loadTransferAvailable}
        handleToggleTransferModal={toggleTransferModal}
        handleToggleAnticipateModal={toggleAnticipationModal}
      />
      <FutureCard />
      {showAntecipate && (
        <AnticipationCard
          anticipationAccessData={anticipationAccessData}
          refetchFeatureAccessData={refetchFeatureAccessData}
          modalAnticipateIsOpen={anticipateModal}
          updateTransferAvailable={loadTransferAvailable}
          handleToggleTransferModal={toggleTransferModal}
          handleToggleAnticipateModal={toggleAnticipationModal}
          handleSetHasValueToAnticipate={setHasValueToAnticipate}
        />
      )}
    </div>
  );
}

export default CardList;
